import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  vatAmount: number;
  totalWithoutVat: number;
  totalWithVat: number;
}

export interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: Date;
  dueDate: Date;
  clientName: string;
  clientAddress: string;
  clientVatNumber: string;
  clientIdNumber: string;
  lineItems: InvoiceLineItem[];
  subtotal: number;
  totalVat: number;
  grandTotal: number;
  notes: string;
  paymentTerms: string;
  memorandumFile?: File;
  useSavedMemorandum?: boolean;
}

@Component({
  selector: 'app-create-invoice-dialog',
  templateUrl: './create-invoice-dialog.component.html',
  styleUrls: ['./create-invoice-dialog.component.css']
})
export class CreateInvoiceDialogComponent implements OnInit {
  invoiceForm: FormGroup;
  defaultVatRate: number = 17; // Default VAT rate 17%
  memorandumFile: File | null = null;
  memorandumFileName: string = '';
  hasSavedMemorandum: boolean = false;
  savedMemorandumFileName: string = '';
  useSavedMemorandum: boolean = false;
  
  constructor(
    public dialogRef: MatDialogRef<CreateInvoiceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.invoiceForm = this.createForm();
  }

  ngOnInit(): void {
    this.addLineItem(); // Add first empty line item
    this.generateInvoiceNumber();

    // Check if a saved memorandum exists (passed from parent)
    if (this.data?.hasSavedMemorandum) {
      this.hasSavedMemorandum = true;
      this.savedMemorandumFileName = this.data.savedMemorandumFileName || 'memorandum.docx';
      this.useSavedMemorandum = true; // Default to using saved memorandum
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      invoiceNumber: ['', Validators.required],
      invoiceDate: [new Date(), Validators.required],
      dueDate: [this.getDefaultDueDate(), Validators.required],
      clientName: ['', Validators.required],
      clientAddress: [''],
      clientVatNumber: [''],
      clientIdNumber: [''],
      lineItems: this.fb.array([]),
      notes: [''],
      paymentTerms: ['Payment due within 15 days']
    });
  }

  get lineItems(): FormArray {
    return this.invoiceForm.get('lineItems') as FormArray;
  }

  createLineItem(): FormGroup {
    return this.fb.group({
      description: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      vatRate: [this.defaultVatRate, [Validators.required, Validators.min(0), Validators.max(100)]],
      vatAmount: [{ value: 0, disabled: true }],
      totalWithoutVat: [{ value: 0, disabled: true }],
      totalWithVat: [{ value: 0, disabled: true }]
    });
  }

  addLineItem(): void {
    this.lineItems.push(this.createLineItem());
  }

  removeLineItem(index: number): void {
    if (this.lineItems.length > 1) {
      this.lineItems.removeAt(index);
      this.calculateTotals();
    }
  }

  calculateLineItem(index: number): void {
    const lineItem = this.lineItems.at(index);
    const quantity = lineItem.get('quantity')?.value || 0;
    const unitPrice = lineItem.get('unitPrice')?.value || 0;
    const vatRate = lineItem.get('vatRate')?.value || 0;

    const totalWithoutVat = quantity * unitPrice;
    const vatAmount = totalWithoutVat * (vatRate / 100);
    const totalWithVat = totalWithoutVat + vatAmount;

    lineItem.patchValue({
      totalWithoutVat: this.roundToTwo(totalWithoutVat),
      vatAmount: this.roundToTwo(vatAmount),
      totalWithVat: this.roundToTwo(totalWithVat)
    });

    this.calculateTotals();
  }

  calculateTotals(): void {
    let subtotal = 0;
    let totalVat = 0;

    this.lineItems.controls.forEach(lineItem => {
      subtotal += lineItem.get('totalWithoutVat')?.value || 0;
      totalVat += lineItem.get('vatAmount')?.value || 0;
    });

    // Store calculated values for display
    this.subtotal = this.roundToTwo(subtotal);
    this.totalVat = this.roundToTwo(totalVat);
    this.grandTotal = this.roundToTwo(subtotal + totalVat);
  }

  // Calculated totals for display
  subtotal: number = 0;
  totalVat: number = 0;
  grandTotal: number = 0;

  roundToTwo(num: number): number {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  getDefaultDueDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 15); // 15 days from now
    return date;
  }

  generateInvoiceNumber(): void {
    // Get the next invoice number from stored sequence
    const year = new Date().getFullYear();
    const storedLastNumber = localStorage.getItem(`lastInvoiceNumber_${year}`);
    const nextNumber = storedLastNumber ? parseInt(storedLastNumber, 10) + 1 : 1;
    
    // Format: YYYY-NNNN (e.g., 2025-0001)
    const invoiceNumber = `${year}-${nextNumber.toString().padStart(4, '0')}`;
    this.invoiceForm.patchValue({ invoiceNumber });
  }

  onMemorandumFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.name.toLowerCase().endsWith('.docx')) {
        this.memorandumFile = file;
        this.memorandumFileName = file.name;
      } else {
        this.snackBar.open('Please select a .docx file', 'Dismiss', {
          duration: 3000,
          panelClass: ['white-snackbar']
        });
        input.value = '';
      }
    }
  }

  removeMemorandum(): void {
    this.memorandumFile = null;
    this.memorandumFileName = '';
  }

  toggleUseSavedMemorandum(useSaved: boolean): void {
    this.useSavedMemorandum = useSaved;
    if (useSaved) {
      this.memorandumFile = null;
      this.memorandumFileName = '';
    }
  }

  saveInvoice(): void {
    if (this.invoiceForm.invalid) {
      this.snackBar.open('Please fill in all required fields', 'Dismiss', {
        duration: 3000,
        panelClass: ['white-snackbar']
      });
      return;
    }

    // Memorandum is required only if no saved memorandum exists or user chose to upload new
    const hasMemorandum = this.memorandumFile || (this.hasSavedMemorandum && this.useSavedMemorandum);
    if (!hasMemorandum) {
      this.snackBar.open('Please upload a memorandum (.docx) file', 'Dismiss', {
        duration: 3000,
        panelClass: ['white-snackbar']
      });
      return;
    }

    // Prepare invoice data
    const formValue = this.invoiceForm.getRawValue();
    const invoiceData: InvoiceData = {
      ...formValue,
      subtotal: this.subtotal,
      totalVat: this.totalVat,
      grandTotal: this.grandTotal,
      memorandumFile: this.memorandumFile || undefined,
      useSavedMemorandum: this.hasSavedMemorandum && this.useSavedMemorandum && !this.memorandumFile
    };

    // Store the last used invoice number
    const year = new Date().getFullYear();
    const currentNumber = parseInt(formValue.invoiceNumber.split('-')[1], 10);
    localStorage.setItem(`lastInvoiceNumber_${year}`, currentNumber.toString());

    this.dialogRef.close(invoiceData);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // Codebook integration - select client from codebook
  onClientSelected(client: any): void {
    this.invoiceForm.patchValue({
      clientName: client.companyName || '',
      clientVatNumber: client.vatNumber || '',
      clientIdNumber: client.idNumber || '',
      clientAddress: client.address || ''
    });
  }
}
